import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FormattedMessage } from "react-intl";
import { DateTime } from "luxon";

import { Resource } from "../../../api/useFetch";
import { ShiftOffer } from "../../shiftOffer";
import CustomDate from "../../../components/Date";
import Price from "../../../components/Price";
import { ServiceClass } from "../../../components/ServiceClass";

import styles from "./ShiftOfferDialog.module.css";
import closeModalIcon from "../../../../static/close-icon.svg";

interface ShiftOfferDialogProps {
  shift: Resource<ShiftOffer> | null;
  isOpen: boolean;
  onClose: () => void;
  onAccept: (shift: Resource<ShiftOffer>) => void;
  isAccepting: boolean;
}

export function ShiftOfferDialog({
  shift,
  isOpen,
  onClose,
  onAccept,
  isAccepting,
}: ShiftOfferDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      dialogRef.current?.focus();
      document.body.classList.add("dialog-open");
    }

    return () => {
      document.body.classList.remove("dialog-open");
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAccept = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shift) {
      return;
    }
    onAccept(shift);
  };

  if (!isOpen || !shift) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  const attrs = shift.attributes;
  const confirmationDescription = attrs.confirmation
    ? interpolateDescription(
        attrs.confirmation.description,
        attrs.starts_at,
        attrs.ends_at,
      )
    : null;

  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shift-offer-dialog-title"
        className={styles.dialog}
        tabIndex={-1}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close dialog"
        >
          <img src={closeModalIcon} alt="" aria-hidden="true" />
        </button>

        <h2 id="shift-offer-dialog-title" className={styles.title}>
          Timeslot details
        </h2>

        <div className={styles.content}>
          <div className={styles.headerDetails}>
            <div className={styles.date}>
              <CustomDate value={attrs.starts_at} isHighlighted />
            </div>
          </div>
          {attrs.subtitle && (
            <div className={styles.subtitle}>{attrs.subtitle}</div>
          )}
          <div className={styles.divider} />

          <div className={styles.property}>
            <div className={styles.propertyLabel}>
              <FormattedMessage id="offer.shift.operating-area" />
            </div>
            <div className={styles.propertyValue}>{attrs.operating_area}</div>
          </div>

          <div className={styles.property}>
            <div className={styles.propertyLabel}>
              <FormattedMessage id="offer.shift.window" />
            </div>
            <div className={styles.propertyValue}>
              {formatTimeRange(attrs.starts_at, attrs.ends_at)} (
              <FormattedMessage
                id="offer.shift.max-duration"
                values={{ hours: attrs.max_shift_duration_hours }}
              />
              )
            </div>
          </div>

          <div className={styles.property}>
            <div className={styles.propertyLabel}>
              <FormattedMessage id="shared.service-class" />
            </div>
            <div className={styles.propertyValue}>
              <ServiceClass id={attrs.service_class} />
            </div>
          </div>

          {attrs.description && (
            <div className={styles.description}>{attrs.description}</div>
          )}
          {confirmationDescription && (
            <div className={styles.description}>{confirmationDescription}</div>
          )}

          {attrs.banner && <div className={styles.banner}>{attrs.banner}</div>}
        </div>
        <div className={styles.divider} />

        <form onSubmit={handleAccept}>
          <div className={styles.actions}>
            <div className={styles.propertyValue}>
              <Price
                currency={attrs.currency}
                value={parseFloat(attrs.price)}
              />
            </div>
            <button type="submit" disabled={isAccepting}>
              <FormattedMessage id="offer.shift.accept" />
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function interpolateDescription(
  template: string,
  startsAt: string,
  endsAt: string,
): string {
  return template
    .replace(/\{start_time\}/g, formatTime(startsAt))
    .replace(/\{end_time\}/g, formatTime(endsAt))
    .replace(/\{date\}/g, formatDate(startsAt));
}

function formatTime(value: string): string {
  return DateTime.fromISO(value, { setZone: true }).toLocaleString({
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value: string): string {
  return DateTime.fromISO(value, { setZone: true }).toLocaleString({
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function formatTimeRange(startsAt: string, endsAt: string): string {
  return `${formatTime(startsAt)} – ${formatTime(endsAt)}`;
}
